import { maxBy } from 'lodash';
import { LouiseQuote } from '../../../../../../types/Quotes';
import useSessionId from '../../../../../flows/src/hooks/useSessionId';
import {
  getFlatProductsResponse,
  useTopProducts,
} from '../../../hooks/useQuoteQueries';
import { clearComparisonMatrix } from '../../../store/redux/slices/offersSlice';
import {
  useLouiseDispatch,
  useLouiseSelector,
} from '../../../store/redux/store';
import { useProducts } from '../../offers/api/getProducts';

interface UseComparisonQuotes {
  comparisonQuotes: LouiseQuote[];
  isFetched: boolean;
}

/**
 * This hook returns the comparison quotes for the given risk object
 * It also includes:
 * - The best offer flag
 * - The possible guarantees
 * @param riskObject The risk object to get the comparison quotes for
 * @returns The comparison quotes for the given risk object
 */
const useComparisonQuotes = (riskObject: string): UseComparisonQuotes => {
  const dispatch = useLouiseDispatch();
  const sessionId = useSessionId();
  const comparisonMatrixOffers = useLouiseSelector(
    (state) => state.offers.comparisonMatrixOffers,
  );

  const comparisonQuotesData = comparisonMatrixOffers[riskObject];

  if (
    comparisonQuotesData &&
    comparisonQuotesData.every((quote: unknown) => typeof quote === 'string')
  ) {
    dispatch(clearComparisonMatrix());
  }

  const { data: products } = useTopProducts(riskObject);
  const { data: productsV2 } = useProducts({
    sessionId,
    insuranceType: riskObject,
  });

  let comparisonQuotes: LouiseQuote[] = [];
  // If all is fetched, all is defined
  if (comparisonQuotesData) {
    // Get the best offer
    const bestOffer = maxBy(
      (comparisonQuotesData as LouiseQuote[])?.map(
        ({ hash_id, matching_score }) => ({
          hash_id,
          matching_score,
        }),
      ),
      'matching_score',
    )?.hash_id;

    // Add best offer flag
    comparisonQuotes = (comparisonQuotesData as LouiseQuote[])?.map(
      (quote) => ({
        ...quote,
        bestOffer: quote.hash_id === bestOffer,
      }),
    ) as LouiseQuote[];
  }

  // Add possible guarantees (so also disabledo nes)
  comparisonQuotes = comparisonQuotes.map((quote) => {
    let product;
    let productV2;
    if (products)
      product = getFlatProductsResponse(products).find(
        ({ hash_id }) => hash_id === quote.hash_id,
      );
    if (productsV2)
      productV2 = productsV2.items.find(
        ({ hash_id }) => hash_id === quote.hash_id,
      );

    return {
      ...quote,
      quality: quote.quality || productV2?.quality || product?.quality,
      possible_guarantees: product ? product.possible_guarantees : [],
    };
  });

  return { comparisonQuotes, isFetched: true };
};

export default useComparisonQuotes;
